import type { WeatherState } from './types';

/** Global event names. Payload types in EventPayloads. */
export const Events = {
  STORY_FLAG: 'story:flag',
  STORY_JUMP: 'story:jump',
  SCENE_ENTER: 'scene:enter',
  SCENE_EXIT: 'scene:exit',
  DIALOGUE_START: 'dialogue:start',
  DIALOGUE_LINE: 'dialogue:line',
  DIALOGUE_END: 'dialogue:end',
  CINEMATIC_START: 'cinematic:start',
  CINEMATIC_END: 'cinematic:end',
  CINEMATIC_SKIPPED: 'cinematic:skipped',
  WEATHER_CHANGE: 'weather:change',
  INTERACT_PROMPT: 'interact:prompt',
  PLAYER_MOVED: 'player:moved',
  BATTLE_START: 'battle:start',
  BATTLE_END: 'battle:end',
  SAVE_WRITTEN: 'save:written',
  SAVE_LOADED: 'save:loaded',
  SETTINGS_CHANGE: 'settings:change',
  AUDIO_UNLOCKED: 'audio:unlocked',
  UI_OPEN: 'ui:open',
  UI_CLOSE: 'ui:close',
  /** Abstract input, emitted by keyboard/pointer/touch layers and consumed by characters/ui. */
  INPUT_CONFIRM: 'input:confirm',
  INPUT_CANCEL: 'input:cancel',
  INPUT_MENU: 'input:menu',
  INPUT_DIR: 'input:dir',
  INPUT_TAP_WORLD: 'input:tapWorld',
  INPUT_FAST_TEXT: 'input:fastText',
} as const;

export interface EventPayloads {
  [Events.STORY_FLAG]: { flag: string; index: number };
  [Events.STORY_JUMP]: { flag: string };
  [Events.SCENE_ENTER]: { key: string };
  [Events.SCENE_EXIT]: { key: string };
  [Events.DIALOGUE_START]: { id: string };
  [Events.DIALOGUE_LINE]: { id: string; speaker: string; text: string };
  [Events.DIALOGUE_END]: { id: string };
  [Events.CINEMATIC_START]: { id: string };
  [Events.CINEMATIC_END]: { id: string };
  [Events.CINEMATIC_SKIPPED]: { id: string };
  [Events.WEATHER_CHANGE]: { state: WeatherState; t: number };
  [Events.INTERACT_PROMPT]: { label?: string; kind?: 'talk' | 'observe' | 'trigger' | 'exit'; id?: string } | null;
  [Events.PLAYER_MOVED]: { map: string; x: number; y: number; facing: string };
  [Events.BATTLE_START]: { id: string };
  [Events.BATTLE_END]: { id: string; outcome: string };
  [Events.SAVE_WRITTEN]: { slot: string };
  [Events.SAVE_LOADED]: { slot: string };
  [Events.SETTINGS_CHANGE]: unknown;
  [Events.AUDIO_UNLOCKED]: undefined;
  [Events.UI_OPEN]: { panel: string };
  [Events.UI_CLOSE]: { panel: string };
  [Events.INPUT_CONFIRM]: { source: 'keyboard' | 'pointer' | 'touch' };
  [Events.INPUT_CANCEL]: { source: 'keyboard' | 'pointer' | 'touch' };
  [Events.INPUT_MENU]: { source: 'keyboard' | 'pointer' | 'touch' };
  [Events.INPUT_DIR]: { x: -1 | 0 | 1; y: -1 | 0 | 1; source: 'keyboard' | 'touch' };
  [Events.INPUT_TAP_WORLD]: { worldX: number; worldY: number };
  [Events.INPUT_FAST_TEXT]: { held: boolean };
}

type Listener = { fn: (...args: unknown[]) => void; ctx?: unknown; once: boolean };

/**
 * Minimal Phaser-independent emitter (so story/save logic is unit-testable in Node).
 * API mirrors the subset of Phaser.Events.EventEmitter used across the project.
 */
export class TypedBus {
  private readonly map = new Map<string, Listener[]>();
  on(event: string, fn: (...args: unknown[]) => void, ctx?: unknown): this { this.add(event, fn, ctx, false); return this; }
  once(event: string, fn: (...args: unknown[]) => void, ctx?: unknown): this { this.add(event, fn, ctx, true); return this; }
  private add(event: string, fn: (...args: unknown[]) => void, ctx: unknown, once: boolean): void {
    const list = this.map.get(event) ?? [];
    list.push({ fn, ctx, once });
    this.map.set(event, list);
  }
  off(event: string, fn?: (...args: unknown[]) => void, ctx?: unknown): this {
    const list = this.map.get(event);
    if (!list) return this;
    const kept = fn ? list.filter((l) => !(l.fn === fn && (ctx === undefined || l.ctx === ctx))) : [];
    if (kept.length) this.map.set(event, kept); else this.map.delete(event);
    return this;
  }
  removeAllListeners(event?: string): this { if (event) this.map.delete(event); else this.map.clear(); return this; }
  emit(event: string, ...args: unknown[]): boolean {
    const list = this.map.get(event);
    if (!list || list.length === 0) return false;
    for (const l of [...list]) {
      if (l.once) this.off(event, l.fn, l.ctx);
      try { l.fn.apply(l.ctx, args); } catch (e) { console.error(`[bus] listener for ${event} threw`, e); }
    }
    return true;
  }
  listenerCount(event: string): number { return this.map.get(event)?.length ?? 0; }
  eventNames(): string[] { return [...this.map.keys()]; }
  emitTyped<K extends keyof EventPayloads>(event: K, payload: EventPayloads[K]): boolean {
    return this.emit(event, payload);
  }
  onTyped<K extends keyof EventPayloads>(event: K, fn: (payload: EventPayloads[K]) => void, ctx?: unknown): this {
    return this.on(event, fn as (...args: unknown[]) => void, ctx);
  }
  onceTyped<K extends keyof EventPayloads>(event: K, fn: (payload: EventPayloads[K]) => void, ctx?: unknown): this {
    return this.once(event, fn as (...args: unknown[]) => void, ctx);
  }
  offTyped<K extends keyof EventPayloads>(event: K, fn?: (payload: EventPayloads[K]) => void, ctx?: unknown): this {
    return this.off(event, fn as (...args: unknown[]) => void, ctx);
  }
}

/** The single global bus. Scenes must remove their listeners on shutdown. */
export const bus = new TypedBus();
